/* eslint-disable no-param-reassign */

import Vue from 'vue';

// 忽略下划线开头的，比如 __ob
const isPreservedKey = k => typeof k === 'string' && k.startsWith('__');
function isHiddenProp(obj, key) {
  const desc = Object.getOwnPropertyDescriptor(obj, key);
  if (desc && !desc.enumerable) return true;
  return isPreservedKey(key);
}
const getRawValue = Symbol('isProxy');
const obProxyCache = new WeakMap();
const getObProxy = ob => {
  if (typeof ob !== 'object' || ob === null) return ob;
  if (obProxyCache.has(ob)) return obProxyCache.get(ob);
  const proxy = new Proxy(ob, {
    get(_, k) {
      const value = ob[k];
      if (k === 'value') return createVueDataProxy(value);
      return value;
    }
  });
  obProxyCache.set(ob, proxy);
  return proxy;
};

/** @type {(fakeTarget: any, realTarget: any) => ProxyHandler<any>}*/
const createProxyHandler = (fakeTarget, realTarget) => {
  let isSealed = false;
  const getSimpleProxyMethod = method => (target, ...args) => Reflect[method](realTarget, ...args);
  const sealFakeTarget = () => {
    if (isSealed) return;
    // if (!Reflect.isExtensible(fakeTarget)) return; // already sealed

    isSealed = true;
    const keys = Reflect.ownKeys(realTarget);
    const wrapGetter = (p, getter) => isPreservedKey(p) ? getter : () => createVueDataProxy(getter());
    if (Array.isArray(realTarget)) {
      fakeTarget.length = 0; // delete [metaKey]
      fakeTarget.length = realTarget.length;
      keys.forEach(k => fakeTarget[k] = createVueDataProxy(realTarget[k]));
    } else {
      delete fakeTarget[metaKey];
      keys.forEach(k => {
        const descriptor = Reflect.getOwnPropertyDescriptor(realTarget, k);
        if ('value' in descriptor) {
          fakeTarget[k] = wrapGetter(k, () => descriptor.value)();
        } else {
          Reflect.defineProperty(fakeTarget, k, {
            configurable: true,
            enumerable: descriptor.enumerable,
            get: wrapGetter(k, descriptor.get),
            set: undefined
          });
        }
      });
    }
    Reflect.preventExtensions(fakeTarget);
  };
  if (!Reflect.isExtensible(realTarget)) sealFakeTarget();
  return {
    get(_, k) {
      if (k === getRawValue) return realTarget;
      if (isSealed) return fakeTarget[k];
      const tmpVal = realTarget[k];
      if (k === '__ob__') return getObProxy(tmpVal); // Vue2.x 中 $set 会访问对象的 __ob__.value 再做修改，为了追踪修改，需要返回一个 Proxy
      if (isHiddenProp(realTarget, k)) return tmpVal;
      return createVueDataProxy(tmpVal);
    },
    set(_, k, v) {
      const vIsObject = typeof v === 'object' && v !== null;
      if (vIsObject && getRawValue in v) v = v[getRawValue]; // 实际写入的时候，如果是一个 Proxy，要拆掉包

      if (isHiddenProp(realTarget, k)) {
        realTarget[k] = v;
        return true;
      }
      const oldVal = listeners && realTarget[k];
      Vue.set(realTarget, k, v);
      if (listeners) emitVueDataProxyChange(realTarget, k, oldVal, v);
      return true;
    },
    deleteProperty(o, k) {
      /* istanbul ignore next */
      if (k === getRawValue) return false;
      if (isHiddenProp(realTarget, k)) return Reflect.deleteProperty(realTarget, k);
      const oldVal = listeners && realTarget[k];
      Vue.delete(realTarget, k);
      if (listeners) emitVueDataProxyChange(realTarget, k, oldVal, undefined);
      return true;
    },
    has(_, k) {
      if (k === getRawValue) return true;
      return k in realTarget;
    },
    ownKeys: getSimpleProxyMethod('ownKeys'),
    defineProperty(_, k, descriptor) {
      const oldVal = listeners && realTarget[k];
      const ans = Reflect.defineProperty(realTarget, k, descriptor);
      Reflect.defineProperty(fakeTarget, k, descriptor);
      if (listeners) {
        const newVal = realTarget[k];
        if (oldVal !== newVal) emitVueDataProxyChange(realTarget, k, newVal);
      }
      return ans;
    },
    getOwnPropertyDescriptor: getSimpleProxyMethod('getOwnPropertyDescriptor'),
    getPrototypeOf: getSimpleProxyMethod('getPrototypeOf'),
    setPrototypeOf: getSimpleProxyMethod('setPrototypeOf'),
    isExtensible() {
      if (isSealed) return false;
      const ans = Reflect.isExtensible(realTarget);
      if (!ans) sealFakeTarget();
      return ans;
    },
    preventExtensions() {
      sealFakeTarget();
      Reflect.preventExtensions(realTarget);
      return true;
    }
  };
};
const cachedProxies = new WeakMap();
const metaKey = Symbol('@@meta');

/**
 *
 * @template T
 * @param {T} root  必须是一个真实的对象
 * @return {T}
 */
export function createVueDataProxy(root) {
  if (typeof root !== 'object' || root === null) return root;
  if (isVueDataProxy(root)) return root;
  let proxy = cachedProxies.get(root);
  if (!proxy) {
    const meta = {
      '@@raw': root
    };
    const stand = Array.isArray(root) // 这是替身攻击！
    ? [meta] : Object.assign(Object.create(Reflect.getPrototypeOf(root)), {
      [metaKey]: meta
    });
    const proxyHandler = createProxyHandler(stand, root);
    proxy = new Proxy(stand, proxyHandler);
    cachedProxies.set(root, proxy);
    cachedProxies.set(stand, proxy);
  }
  return proxy;
}

/**
 * @typedef {(obj: any, key: any, oldValue: any, newValue: any)} VueDataProxyOnChangeListener
 */

/** @type {null | VueDataProxyOnChangeListener[]} */
let listeners = null;
function emitVueDataProxyChange(obj, key, oldValue, newValue) {
  if (!listeners) return;
  if (oldValue === newValue) return;
  listeners.forEach(cb => {
    try {
      cb(obj, key, oldValue, newValue);
    } catch (err) {
      console.error('[VueDataProxyOnChangeListener]', err);
    }
  });
}

/**
 * 全局监听
 *
 * @param {VueDataProxyOnChangeListener} callback
 * @returns {() => void} 停止监听的函数
 */
export function subscribeVueDataProxyChange(callback) {
  if (listeners === null) listeners = [callback];else listeners.push(callback);
  return () => {
    if (!listeners) return;
    const idx = listeners.indexOf(callback);
    if (idx === -1) return;
    listeners.splice(idx, 1);
    if (listeners.length === 0) listeners = null;
  };
}
export function isVueDataProxy(v) {
  return typeof v === 'object' && v !== null ? v[getRawValue] : undefined;
}